import { defineAsyncComponent, reactive, toRaw } from "vue";
import { has } from "../../../../../utils/menu-context";
import { Button, Col, Modal, Row } from "ant-design-vue";
import store from "../../../../../store";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import i18n from "../../../../../i18n";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import { createColumnInput, createColumnSelect } from "../../../../../components/table/c-table.logic";
import {
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../../utils/form";
import {
    createFieldInput,
    createFieldSelect
} from "../../../../../components/input/template-builder/template-builder.logic";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { createLoading } from "../../../../../utils/loading";
import { required, validateError } from "../../../../../utils/validate";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView
} from "../../../../../utils/page";

import { callSearchLookup, getLabType, getLabTypeByKey, getLabTypeKeyByRoleMenuAttributes } from "../lab-share.logic";
import lodash from "lodash";
import {
    callUpdateAll,
    fetchTopics
} from "../lab-monitor-product-group-management/lab-monitor-product-group-topic-managment.logic";
export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/master/labMonitorProductGroup";
export const routerPrefix = "lab-monitor-product-groups";

export const callSave = ({ form }) => {return store.dispatch(`${actionPrefix}/save`, { data: form });};
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });

export const callLabType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LAB_TYPE", value: searchQuery });

export const callSamplingMonitorType = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/samplingMonitorType/cacheFindAll", { searchQuery });

const CIcon = defineAsyncComponent(() => import('../../../../../components/icon/CIcon'))
const LabMonitorProductGroupTopicManagement = defineAsyncComponent(() => import('../lab-monitor-product-group-management/LabMonitorProductGroupTopicManagement'))

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onSearch = search => {
    setIsLoading(true);
    const { page, props } = search;
    const { where = [] } = page;

    let whereLabTypes = [];
    const labTypeKeys = getLabTypeKeyByRoleMenuAttributes({ props });
    if (labTypeKeys.length > 0) {
        whereLabTypes = [
            { openBucket: true, name: "refLabType.key", operator: "in", type: "String", value: labTypeKeys },
            { closeBucket: true, logic: "AND", name: "refLabType.group", operator: "=", type: "String", value: "LAB_TYPE" }
        ];
    }

    window.onSearch?.({ page });

    return callSearch({
        criteria: {
            ...page,
            select: ["**"],
            where: [...where, ...whereLabTypes],
            data: []
        }
    })
        .then(page => (state.page = { ...page }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: data.useForm.modelRef })
                .finally(() => setIsLoadingApp(false))
                .then(record => onClickView({ record, routerPrefix }));
        })
        .catch(error => validateError({ error }));
};

export const initState = async ({ props, data }) => {
    const menuContext = props.menuContext;
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];

    const labTypeKeys = await getLabTypeByKey({ searchQuery: data.labAttributes[0] });
    data.searchLabType = labTypeKeys[0];
    state.columns = createColumns({ props, data });

    const onSearchCustom = search => onSearch({ ...search, props });

    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) => onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: onSearchCustom, setIsLoading })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
            onClickArchiveAll({
                 rowSelection, callArchiveAll, state, onSearch: onSearchCustom, setIsLoading })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) => onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: onSearchCustom, setIsLoading })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({ menuContext, onClick: ({ record }) => onClickEdit({ record, routerPrefix }) })
    ];
};

export const createColumns = ({ props, data }) => {
    const {menuContext} = props
    return [
        createColumnInput({
            title: () => i18n.global.t("model.labMonitorProductGroup.name"),
            dataIndex: "name",
            align: "center"
        }),
        createColumnInput({
            title: () => i18n.global.t("model.labMonitorProductGroup.refSamplingMonitorTypeCode"),
            dataIndex: "refSamplingMonitorType.code",
            align: "center",
            search: { visible: () => false }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.labMonitorProductGroup.refSamplingMonitorType"),
            dataIndex: "refSamplingMonitorType.name",
            inputProp: ({ field }) => {
                return {
                    value: field.searchValue,
                    "onUpdate:value": value => (field.searchValue = value),
                    list: callSamplingMonitorType,
                    renderLabel: ({ item }) => <div>{item.code} - {item.name}</div>,
                };
            },
            align: "center",
            search: { name: "refSamplingMonitorType.id"}
        }),
        {
            menuContext,
            visible: () => false,
            title: () => i18n.global.t('lab.labStandardTopic'),
            customRender: ({record}) => <Button class="btn-primary"
                onClick={() => openManageTopicModal({props, state, record, labType: data.refLabType})}
            ><CIcon file='edit.svg'/>{i18n.global.t('lab.countryStandardGroupTopic.modal.manageStandardTopic')}
            </Button>,
            showText: () => false,
            sorter: false,
            width: 150,
            align: 'center',
            search: {visible: () => false},
        },
        createColumnSelect({
            visible: () => false,
            title: () => i18n.global.t("model.lookup.modelGroup.LAB_TYPE"),
            customRender: ({ record }) => <div>{record.refLabType?.value}</div>,
            dataIndex: "refLabType.value",
            inputProp: ({field}) => {
                return {
                    visible: () => data.labAttributes.length == 1,
                    list: async ({ searchQuery }) => {
                        const labTypes = await getLabType({ searchQuery });
                        const labTypesFiltered = labTypes.filter(item => data.labAttributes.includes(item?.key));
                        return labTypesFiltered;
                    },
                    "onUpdate:value": value => {
                        data.searchLabType = value;
                        field.searchValue = value;
                    },
                    optLabel: "value"
                };
            },
            search: {
                name: "refLabType.id",
                visible: () => data.labAttributes?.length > 1,
                field: {
                    searchValue: data.searchLabType,
                    rulesRef: [required]
                }
            },
            align: "center"
        })
    ];
};

function isEditValue(data, originalData) {
    let isEdit = false
    data.forEach(it => {
        const labFormula = it?.refLabTopic?.refLabFormula?.key
        if (labFormula === 'Operator' || labFormula === 'n c m M' || labFormula === 'Detected') {
            originalData.forEach(ori => {
                if (ori.id === it.id) {
                    if (ori.c !== it.c || ori.n !== it.n || ori.m !== it.m
                        || ori.mUpper !== it.mUpper || ori.mathSymbol !== it.mathSymbol
                        || ori.operatorValue !== it.operatorValue
                        || ori.isDetected !== it.isDetected
                    ) {
                        isEdit = true
                    }
                }
            })
        }
    })
    return isEdit
}

function isNotNumber(it) {
    return !lodash.isEmpty(it) && typeof (it) == "string";
}

function parseNumberValue(items) {
    return items?.map(it => {
        if (isNotNumber(it.n)) {
            it.n = parseInt(it.n)
        }
        if (isNotNumber(it.c)) {
            it.c = parseInt(it.c)
        }
        if (isNotNumber(it.m)) {
            it.m = parseInt(it.m)
        }
        if (isNotNumber(it.mUpper)) {
            it.mUpper = parseInt(it.mUpper)
        }
        if (isNotNumber(it.operatorValue)) {
            it.operatorValue = parseFloat(it.operatorValue)
        }
        return it
    });
}

function confirmManageTopicModal(items, refs, page, record, options) {
    Modal.confirm({
        title: i18n.global.t('components.table.operatorAndPagination.youAreWant', {text: i18n.global.t('button.save')}),
        autoFocusButton: 'cancel',
        onCancel: () => {
            setIsLoadingApp(false)
        },
        onOk: () => {
            setIsLoading(true)
            const data = parseNumberValue(items)
            callUpdateAll({data: data ? data : []}).finally(() => {
                setIsLoading(false)
                fetchTopics({mapperState: refs.data.mapperState, page, id: record.id}).then(() => (options?.bind?.onCancel()))
            })
        },
    });
}

export const openManageTopicModal = ({props, record}) => {
    const refs = reactive({data: null})
    const options = addModal({
        bind: lodash.merge({width: "80%"}, {
            okButtonProps: {disabled: props.viewOnly},
            onOk: () => {
                const page = refs.data.mapperState.page
                const items = page?.data || []
                const isEdit = isEditValue(items, refs.data.mapperState.originalData)
                if (!isEdit || items.length === 0) {
                    options.bind.onCancel()
                    return
                }
                confirmManageTopicModal(items, refs, page, record, options);
            }
        }),
        render: () =>
            <CardBorder title={i18n.global.t('model.labMonitorProductGroupTopic.modal.manageStandardTopic')}>
                <LabMonitorProductGroupTopicManagement ref={(e) => {
                    if (e) refs.data = e
                }} {...props} record={record}
                ></LabMonitorProductGroupTopicManagement>
            </CardBorder>
    })
}

export const state = reactive({
    columns: [],
    page: createPage({
        options: {
            select: ["*"]
        }
    }),
    form: {
        refSamplingMonitorType: null
    },
    tableOptions: { search: { activeKey: "Search" } }
});

export const createForm = () => {
    const refSamplingMonitorType = state?.form?.refSamplingMonitorType || null
    return {
        name: null,
        refLabType: null,
        refSamplingMonitorType: refSamplingMonitorType,
        topics: [],
    };
};

export const createRules = () => {
    return {
        name: [required],
        refLabType: [required],
        refSamplingMonitorType: [required],
        topics: [],
    };
};

export const createField = ({ props, data, isRoleMultipleLabType, labTypeKeys }) => {
    const viewOnly = props.viewOnly;
    const useForm = data.useForm;
    const modelRef = useForm.modelRef;
    return [
        createFieldSelect({
            visible: () => data.labAttributes?.length !== 1,
            key: `refLabType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.labMonitorProductGroup.refLabType"),
            inputProp: () => ({
                value: modelRef.refLabType,
                "onUpdate:value": value => {
                    modelRef.refLabType = value
                    modelRef.refSamplingMonitorType = null;
                    useForm.resetFields(modelRef)
                },
                disabled: viewOnly || !isRoleMultipleLabType,
                list: async ({ searchQuery }) => {
                    const response = await callLabType({ searchQuery });
                    const data = response.filter(labType => labTypeKeys.includes(labType.key));
                    return data;
                },
                optLabel: "value"
            }),
            changeLog: { getValue: ({ record }) => record.refLabType?.value }
        }),
        createFieldSelect({
            key: `refSamplingMonitorType`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.labMonitorProductGroup.refSamplingMonitorType"),
            inputProp: () => ({
                value: modelRef.refSamplingMonitorType,
                list: callSamplingMonitorType,
                "onUpdate:value": value => {
                    modelRef.refSamplingMonitorType = value
                    state.form.refSamplingMonitorType = value
                },
                optLabel: "code",
                disabled: !modelRef.refLabType || viewOnly,
                renderLabel: ({ item }) => <div>{ item.code } - { item.name}</div>,
            }),
            changeLog: { getValue: ({ record }) => `${record.refSamplingMonitorType?.code} ${record.refSamplingMonitorType?.name}` }
        }),
        createFieldInput({
            key: "name",
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t("model.labMonitorProductGroup.name"),
            inputProp: () => ({ value: modelRef.name, "onUpdate:value": value => (modelRef.name = value), disabled: viewOnly })
        }),
        createFieldSelect({
            visible: () => false,
            key: `topics`,
            colProp: () => ({ xs: 24 }),
            formItemProp: () => ({ labelCol: { xs: 24, md: 6 }, wrapperCol: { xs: 24, md: 18 } }),
            label: () => i18n.global.t('lab.labTopic'),
            inputProp: () => ({
                value: modelRef.topics,
                'onUpdate:value': value => {
                    modelRef.topics = value
                },
                disabled: viewOnly,
                list: ({ searchQuery }) => {
                    const where = []
                    if (modelRef.refLabType?.key === 'isPhysic') {
                        where.push({name: "refParent.id", type: "UUID", operator: '=', value: null})
                    }
                    return store.dispatch('resource/v1/master/labTopic/cacheFindAll', { searchQuery, labTypeKey: modelRef.refLabType?.key, where }).then((items) => items.map(item => ({ refLabTopic: item })))
                },
                optKey: 'refLabTopic.id',
                renderLabel: ({ item }) => {
                    return <span>{item?.refLabTopic?.name}</span>
                },
                mode: 'multiple',
            }),
            changeLog: { getValueChildren: ({record}) => record.topics?.name }
        })
    ];
};

export const createFormTitle = ({ data }) => {
    return data.useForm?.modelRef?.id ? data.useForm.modelRef.name : i18n.global.t("button.create");
};

export const createAppPageHeaderDefault = ({ props, data }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly } = props;

    const { id, isActive } = data.useForm.modelRef;

    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && has({ menuContext, key: "canEdit" })) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ data }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }

    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [
                {
                    name: 'topics',
                    select: ['**'],
                    page: 0,
                    size: 10000,
                    sort: [{ name: 'refLabTopic.name', type: 'ASC' }],
                }
            ],
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [modelRef] });
    }
    return promise.then(page => initUseForm({ data, page, routerPrefix })).finally(() => setIsLoading(false));
};

export const initForm = async ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.labAttributes = getLabTypeKeyByRoleMenuAttributes({ props }) || [];
    data.form = createForm();
    data.rules = createRules();

    const labTypeKeys = getLabTypeKeyByRoleMenuAttributes({ props });
    if (!props.viewOnly) {
        if (labTypeKeys.length == 1) {
            const response = await callSearchLookup({
                criteria: {
                    select: ["*"],
                    page: 0,
                    size: 20,
                    where: [
                        { name: "group", operator: "=", type: "String", value: "LAB_TYPE" },
                        { name: "key", operator: "=", type: "String", value: labTypeKeys[0] }
                    ]
                }
            });
            data.form.refLabType = response.data[0];
        }
    }

    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isRoleMultipleLabType: labTypeKeys.length > 1, labTypeKeys });

    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};
